import { Component, inject, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPopover,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  bugOutline,
  chatbubbleEllipsesOutline,
  heartOutline,
  helpBuoyOutline,
  helpCircleOutline,
} from 'ionicons/icons';

import { FEEDBACK_KINDS, FeedbackService } from '../../features/feedback/feedback.service';

/**
 * The (?) in the top bar: Help → Send feedback, Report a bug, Ask a question, Send praise.
 *
 * <p>Each item opens the feedback page with its kind chosen and the page the person was on attached,
 * so "it broke here" arrives with "here". The whole button is hidden when there is nowhere to send
 * feedback — a host whose owner has not flagged an intake board — rather than offering a form that
 * can only fail. When the Help menu grows items that are not feedback, only the feedback items
 * should hide.</p>
 */
@Component({
  selector: 'app-help-menu',
  standalone: true,
  imports: [IonButton, IonIcon, IonPopover, IonList, IonItem, IonLabel],
  template: `
    @if (feedback.available()) {
      <ion-button id="help-menu" aria-label="Help">
        <ion-icon slot="icon-only" name="help-circle-outline"></ion-icon>
      </ion-button>

      <ion-popover trigger="help-menu" triggerAction="click" [dismissOnSelect]="true">
        <ng-template>
          <ion-list lines="none">
            @for (option of kinds; track option.kind) {
              <ion-item button [detail]="false" (click)="open(option.slug)">
                <ion-icon slot="start" [name]="option.icon"></ion-icon>
                <ion-label>{{ option.menuLabel }}</ion-label>
              </ion-item>
            }
          </ion-list>
        </ng-template>
      </ion-popover>
    }
  `,
})
export class HelpMenuComponent {
  protected readonly feedback = inject(FeedbackService);
  private readonly router = inject(Router);

  protected readonly kinds = FEEDBACK_KINDS;

  private readonly popover = viewChild(IonPopover);

  constructor() {
    addIcons({
      helpCircleOutline,
      chatbubbleEllipsesOutline,
      bugOutline,
      helpBuoyOutline,
      heartOutline,
    });
  }

  protected async open(slug: string): Promise<void> {
    await this.popover()?.dismiss();
    await this.router.navigate(['/feedback'], {
      // Not from the feedback page itself: "back to where you were" would be back to the form.
      queryParams: {
        type: slug,
        from: this.router.url.startsWith('/feedback') ? null : location.href,
      },
    });
  }
}
